/**
 * Utilidades para mostrar fechas en la UI sin sufrir desfase por
 * zona horaria.
 *
 * Contexto del bug que motivó este archivo
 * ----------------------------------------
 * El backend almacena `fecha_entrega`/`fecha_pedido` como DATE en
 * MySQL. mysql2 las retorna a Node como `Date` JS construido en UTC
 * (medianoche UTC del día). Express las serializa a JSON como
 * "2026-05-17T00:00:00.000Z".
 *
 * Si el frontend hace `new Date("2026-05-17T00:00:00.000Z")` y luego
 * `.toLocaleDateString('es-CL')`, el cliente en Chile (UTC-4) ve el
 * día anterior porque las 00:00 UTC son las 20:00 del día previo en
 * Chile.
 *
 * Solución: NO construir un Date desde el string ISO con T. Tomar
 * la parte YYYY-MM-DD y construir un Date *local* con
 * `new Date(year, month - 1, day)`. Ese Date representa "el día X
 * a las 00:00 hora local del navegador", y `toLocaleDateString` lo
 * formatea sin shift.
 *
 * Cualquier formateo de DATE de BD debe pasar por uno de estos
 * helpers. NO uses `new Date(string).toLocaleDateString` directo.
 */

type EntradaFecha = string | Date | null | undefined;

/**
 * Saca {y, m, d} de la entrada sin disparar conversión TZ.
 * - String "YYYY-MM-DD" o "YYYY-MM-DDTHH:mm:ss…": toma sólo la
 *   parte de fecha.
 * - Date: usa los getters UTC porque el backend envía el día como
 *   medianoche UTC. getUTCDate() devuelve el día calendario real.
 */
const parsearComponentes = (
  input: EntradaFecha
): { y: number; m: number; d: number } | null => {
  if (input === null || input === undefined || input === '') return null;
  if (input instanceof Date) {
    if (isNaN(input.getTime())) return null;
    return {
      y: input.getUTCFullYear(),
      m: input.getUTCMonth() + 1,
      d: input.getUTCDate(),
    };
  }
  const s = String(input);
  const soloFecha = s.includes('T') ? s.split('T')[0] : s;
  const partes = soloFecha.split('-');
  if (partes.length < 3) return null;
  const y = Number(partes[0]);
  const m = Number(partes[1]);
  const d = Number(partes[2]);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) {
    return null;
  }
  return { y, m, d };
};

const construirDateLocal = (y: number, m: number, d: number): Date =>
  new Date(y, m - 1, d);

/**
 * Formato largo con día de la semana: "lunes, 11 de mayo de 2026".
 * Ideal para vistas de detalle.
 */
export const formatearFechaLarga = (input: EntradaFecha): string => {
  const c = parsearComponentes(input);
  if (!c) return '';
  return construirDateLocal(c.y, c.m, c.d).toLocaleDateString('es-CL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Formato medio: "11 may 2026". Para tarjetas y tablas.
 */
export const formatearFechaMedia = (input: EntradaFecha): string => {
  const c = parsearComponentes(input);
  if (!c) return '';
  return construirDateLocal(c.y, c.m, c.d).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * Formato corto numérico: "11/05/2026". Para listados densos.
 */
export const formatearFechaCorta = (input: EntradaFecha): string => {
  const c = parsearComponentes(input);
  if (!c) return '';
  return construirDateLocal(c.y, c.m, c.d).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Alias legacy — usado por CocinaSimple. Equivalente a Larga.
 * Mantener para no romper imports existentes.
 */
export const formatearFechaUTC = formatearFechaLarga;

/**
 * Convierte fecha local (YYYY-MM-DD) a string ISO con mediodía UTC.
 * Usado por algunos endpoints que esperan ISO con tiempo.
 */
export const convertirFechaLocalAUTC = (fechaLocal: string): string => {
  return `${fechaLocal}T12:00:00.000Z`;
};

/**
 * Fecha actual del usuario en formato YYYY-MM-DD, zona local del
 * navegador. NO usar `new Date().toISOString().split('T')[0]` porque
 * eso devuelve la fecha UTC, no la del usuario.
 */
export const obtenerFechaActualLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * @deprecated Usar obtenerFechaActualLocal — el nombre "UTC" era
 * engañoso. Se mantiene para no romper imports antiguos.
 */
export const obtenerFechaActualUTC = obtenerFechaActualLocal;

/**
 * Días de diferencia entre dos fechas. Resta `fecha2 - fecha1`.
 */
export const calcularDiasEntreFechas = (
  fecha1: EntradaFecha,
  fecha2: EntradaFecha
): number => {
  const c1 = parsearComponentes(fecha1);
  const c2 = parsearComponentes(fecha2);
  if (!c1 || !c2) return 0;
  const d1 = construirDateLocal(c1.y, c1.m, c1.d).getTime();
  const d2 = construirDateLocal(c2.y, c2.m, c2.d).getTime();
  return Math.ceil((d2 - d1) / (1000 * 60 * 60 * 24));
};
